/**
 * Shared `GoalStatus` subsets used by predicates across the puck rendering
 * pipeline. Extracted 2026-05-15 once 4+ sites needed the same definition.
 *
 * Adding a new subset here is preferable to inlining a `new Set([...])`
 * at the call site — keeps the canonical definition of "terminal" /
 * "non-terminal" / etc. in one place, which matters when future status
 * additions (e.g. a new transient state) need to be reconciled across
 * every consumer.
 */

import { GoalStatus } from "@/features/ryteTracker/types/goal";

/**
 * Terminal statuses for any goal — the goal's lifecycle has reached an
 * end state and no further engine transitions occur. CANCELED + FAILED +
 * COMPLETED are all "done" from the engine's perspective.
 *
 * Used by:
 *   - `rebuildPuckFromGoals` (legacyEndMs Math.max) — exclude terminal
 *     goals so a canceled/failed goal's window doesn't define the right
 *     edge of an otherwise-live puck
 *   - `completedWithoutRemovalRule.puckHasRemovalGoal` — exclude terminal
 *     REMOVEs from the "puck has REMOVE constituent" extension gate
 *   - `removeInProgressExtensionRule.findNonTerminalRemove` — exclude
 *     terminal REMOVEs when looking for the in-progress REMOVE to anchor
 *     the pill at nowMs
 */
export const TERMINAL_GOAL_STATUSES: ReadonlySet<GoalStatus> = new Set([
  GoalStatus.COMPLETED,
  GoalStatus.FAILED,
  GoalStatus.CANCELED,
]);

/** True only when a non-empty live goal set is entirely CANCELED. */
export const areAllGoalsCanceled = (goals: readonly { status: GoalStatus }[]): boolean =>
  goals.length > 0 && goals.every((goal) => goal.status === GoalStatus.CANCELED);

/** Convenience predicate — `g.status` is in the terminal set. */
export const isTerminalStatus = (status: GoalStatus): boolean => TERMINAL_GOAL_STATUSES.has(status);

/**
 * Statuses that mark a goal as drag-editable on the timeline. CREATED + HELD
 * are the two states where an operator can legitimately reschedule or move
 * the goal — every other status is either non-actionable (terminal) or
 * already in motion (ACTIVATING / ACTIVE).
 *
 * Used by `isGoalEditable` to gate `editable: true` on timeline items.
 */
export const EDITABLE_GOAL_STATUSES: ReadonlySet<GoalStatus> = new Set([
  GoalStatus.CREATED,
  GoalStatus.HELD,
]);

/** Convenience predicate — `g.status` is drag-editable (CREATED or HELD). */
export const isEditableStatus = (status: GoalStatus): boolean => EDITABLE_GOAL_STATUSES.has(status);

/**
 * Wire-UPDATABLE statuses. Deliberately NOT `EDITABLE_GOAL_STATUSES`, whose
 * `{CREATED, HELD}` membership coincides today: that set answers "is this puck
 * drag-editable on the timeline", this one answers "will the engine accept an
 * `updateGoal` for it". Two concepts, one accidental overlap — keep separate.
 *
 * Mirrors `UpdateGoalLogic.UPDATABLE_STATUSES` in RYARYTEEngine: a non-member
 * lands in the reject arm (`audit=UpdateGoal, action=rejected,
 * reason=status_not_updatable`), so every gate in front of an `updateGoal`
 * branch must ask THIS question, not the drag one.
 *
 * Relocated here 2026-08-24 from
 * `utils/actionModals/ibDoorTransfer/resolvePlannedTransferGraph.ts`, where it
 * was file-local while consumers outside the IB-transfer feature already needed
 * it (`getPuckActionItems`' Stage Trailer gate, `StageTrailerModal`). Its
 * "deliberately not the drag set" rationale only pays off where the two sets sit
 * side by side, which is here.
 */
export const UPDATABLE_GOAL_STATUSES: ReadonlySet<GoalStatus> = new Set([
  GoalStatus.CREATED,
  GoalStatus.HELD,
]);

/** Convenience predicate — the engine will accept an `updateGoal` for this status. */
export const isUpdatableGoalStatus = (status: GoalStatus): boolean =>
  UPDATABLE_GOAL_STATUSES.has(status);
